import {
  promptReasoningEffortsFor,
  type AiModels,
  type AiPromptModelSettings,
  type ReasoningEffort
} from "@pages/main/scripts/prompts/prompt.models"
import type { BaseResponseSchema } from "@pages/main/scripts/prompts/sub_components/ResponseSchemaBuilder"
import { runPromptOnce } from "./api"
import { evaluateExpectations } from "./assertions"
import type {
  AssertionResult,
  CaseAttempt,
  CaseResult,
  JudgeAssertion,
  PlaygroundRun,
  PlaygroundRequest,
  PlaygroundResponse,
  PromptTestCase,
  PromptTestSuite,
  RunProgress,
  RunSummary,
  SuiteDefaults
} from "./types"

export const DEFAULT_JUDGE_MODEL = "gpt-4.1-mini" as AiModels

const JUDGE_SYSTEM_PROMPT = `You are a strict QA judge evaluating a single answer produced by an LLM prompt.
You are given CRITERIA, the USER_QUERY and the ANSWER.
Score from 0 to 10 how well the ANSWER satisfies the CRITERIA (10 = fully satisfies).
Be harsh: missing information, wrong language, wrong format or hallucinations must lower the score.
Answer only with the JSON object.`

const JUDGE_SCHEMA: BaseResponseSchema = {
  type: "object",
  properties: {
    score: { type: "number" },
    reason: { type: "string" }
  },
  required: ["score", "reason"]
}

export function hashText(text: string): string {
  let hash = 5381
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 33) ^ text.charCodeAt(i)
  }
  return (hash >>> 0).toString(16)
}

export function makeId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`
}

export function extractVariableNames(text: string): string[] {
  const found = new Set<string>()
  const pattern = /\{\{\s*([\w.-]+)\s*\}\}/g
  let match = pattern.exec(text ?? "")
  while (match) {
    found.add(match[1])
    match = pattern.exec(text ?? "")
  }
  return Array.from(found)
}

/** `{{#if_x}}…{{/if_x}}` block names — rendered server-side by renderConditionalBlocks. */
export function extractConditionNames(text: string): string[] {
  const found = new Set<string>()
  const pattern = /\{\{#if_([\w-]+)\}\}/g
  let match = pattern.exec(text ?? "")
  while (match) {
    found.add(match[1])
    match = pattern.exec(text ?? "")
  }
  return Array.from(found)
}

export interface PromptDraft {
  name: string
  systemPrompt: string
  model: AiModels
  /** The editor row's pinned settings; the playground's own max-tokens field still wins. */
  modelSettings?: AiPromptModelSettings | null
  responseSchema?: BaseResponseSchema | null
}

export interface RunOptions {
  draft: PromptDraft
  defaults?: SuiteDefaults
  baseVariables?: Record<string, string>
  baseConditions?: Record<string, boolean>
  temperature?: number
  maxTokens?: number
  timeoutMs?: number
  repeats: number
  concurrency: number
  judgeModel?: AiModels
}

/** A model override in the playground may not take the row's effort. */
function draftReasoningEffort(draft: PromptDraft): ReasoningEffort | undefined {
  const effort = draft.modelSettings?.reasoningEffort
  return effort && promptReasoningEffortsFor(draft.model).includes(effort)
    ? effort
    : undefined
}

export function buildRequest(
  options: RunOptions,
  testCase: Pick<
    PromptTestCase,
    "query" | "variables" | "conditions" | "previousMessages"
  >
): PlaygroundRequest {
  return {
    systemPrompt: options.draft.systemPrompt,
    model: options.draft.model,
    responseSchema: options.draft.responseSchema ?? null,
    responseSchemaName: options.draft.name || undefined,
    query: testCase.query,
    variables: {
      ...(options.defaults?.variables ?? {}),
      ...(options.baseVariables ?? {}),
      ...(testCase.variables ?? {})
    },
    conditions: {
      ...(options.defaults?.conditions ?? {}),
      ...(options.baseConditions ?? {}),
      ...(testCase.conditions ?? {})
    },
    previousMessages: testCase.previousMessages?.length
      ? testCase.previousMessages
      : undefined,
    temperature: options.temperature ?? options.defaults?.temperature,
    maxTokens:
      options.maxTokens ??
      options.defaults?.maxTokens ??
      options.draft.modelSettings?.maxOutputTokens ??
      undefined,
    reasoningEffort: draftReasoningEffort(options.draft),
    timeoutMs: options.timeoutMs ?? options.defaults?.timeoutMs
  }
}

async function judgeAnswer(
  judge: JudgeAssertion,
  query: string,
  answer: string,
  fallbackModel: AiModels
): Promise<{ assertion: AssertionResult; cost: number }> {
  const response = await runPromptOnce({
    systemPrompt: JUDGE_SYSTEM_PROMPT,
    model: judge.model ?? fallbackModel,
    responseSchema: JUDGE_SCHEMA,
    responseSchemaName: "PromptJudge",
    query: JSON.stringify({
      CRITERIA: judge.criteria,
      USER_QUERY: query,
      ANSWER: answer
    })
  })

  const minScore = judge.minScore ?? 7
  if (!response.ok) {
    return {
      cost: response.usage.amount,
      assertion: {
        label: `judge >= ${minScore}`,
        passed: false,
        detail: `judge failed: ${response.error}`
      }
    }
  }

  const verdict = (response.parsed ?? {}) as { score?: number; reason?: string }
  const score = Number(verdict.score ?? 0)

  return {
    cost: response.usage.amount,
    assertion: {
      label: `judge >= ${minScore}`,
      passed: score >= minScore,
      detail: `score ${score}/10 — ${verdict.reason ?? ""}`
    }
  }
}

async function runAttempt(
  options: RunOptions,
  testCase: PromptTestCase,
  onJudgeCost: (cost: number) => void
): Promise<CaseAttempt> {
  const response: PlaygroundResponse = await runPromptOnce(
    buildRequest(options, testCase)
  )

  const assertions = evaluateExpectations(response, testCase.expect)

  if (response.ok && testCase.expect?.judge) {
    const { assertion, cost } = await judgeAnswer(
      testCase.expect.judge,
      testCase.query,
      response.output,
      options.judgeModel ?? DEFAULT_JUDGE_MODEL
    )
    onJudgeCost(cost)
    assertions.push(assertion)
  }

  return {
    ok: response.ok,
    error: response.error,
    output: response.output,
    parsed: response.parsed,
    latencyMs: response.latencyMs,
    usage: response.usage,
    assertions,
    passed: response.ok && assertions.every((a) => a.passed)
  }
}

async function withPool<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>
): Promise<void> {
  let cursor = 0
  const size = Math.max(1, Math.min(limit, items.length || 1))

  const runners = Array.from({ length: size }, async () => {
    for (;;) {
      const index = cursor
      cursor += 1
      if (index >= items.length) return
      await worker(items[index], index)
    }
  })

  await Promise.all(runners)
}

function summarize(results: CaseResult[], extraCost: number): RunSummary {
  const active = results.filter((r) => !r.skipped)
  const attempts = active.flatMap((r) => r.attempts)
  const passed = active.filter((r) => r.passed).length
  const errored = active.filter((r) => r.attempts.some((a) => !a.ok)).length
  const totalLatency = attempts.reduce((sum, a) => sum + a.latencyMs, 0)

  return {
    total: active.length,
    passed,
    failed: active.length - passed,
    errored,
    skipped: results.length - active.length,
    passRate: active.length ? Math.round((passed / active.length) * 100) : 0,
    avgLatencyMs: attempts.length
      ? Math.round(totalLatency / attempts.length)
      : 0,
    totalCost:
      attempts.reduce((sum, a) => sum + (a.usage?.amount ?? 0), 0) + extraCost,
    totalTokens: attempts.reduce(
      (sum, a) =>
        sum + (a.usage?.inputToken ?? 0) + (a.usage?.outputToken ?? 0),
      0
    )
  }
}

export async function runSuite(
  suite: PromptTestSuite,
  options: RunOptions,
  hooks?: {
    onProgress?: (progress: RunProgress) => void
    shouldStop?: () => boolean
  }
): Promise<PlaygroundRun> {
  const startedAt = new Date()
  const cases = suite.cases ?? []
  const results: CaseResult[] = cases.map((testCase) => ({
    caseId: testCase.id,
    name: testCase.name,
    query: testCase.query,
    variables: testCase.variables,
    conditions: testCase.conditions,
    tags: testCase.tags,
    skipped: testCase.skip,
    attempts: [],
    passed: false,
    passCount: 0
  }))

  let done = 0
  let judgeCost = 0
  const total = cases.filter((c) => !c.skip).length * options.repeats

  await withPool(cases, options.concurrency, async (testCase, index) => {
    if (testCase.skip) return

    const attempts: CaseAttempt[] = []
    for (let repeat = 0; repeat < options.repeats; repeat += 1) {
      if (hooks?.shouldStop?.()) break
      // eslint-disable-next-line no-await-in-loop
      const attempt = await runAttempt(options, testCase, (cost) => {
        judgeCost += cost
      })
      attempts.push(attempt)
      done += 1
      hooks?.onProgress?.({ done, total, currentCase: testCase.name })
    }

    const passCount = attempts.filter((a) => a.passed).length
    results[index] = {
      ...results[index],
      attempts,
      passCount,
      passed: attempts.length > 0 && passCount === attempts.length
    }
  })

  const finishedAt = new Date()

  return {
    id: makeId("run"),
    label: `${options.draft.model} · ${finishedAt.toLocaleString("tr-TR")}`,
    promptName: options.draft.name,
    startedAt: startedAt.toISOString(),
    finishedAt: finishedAt.toISOString(),
    durationMs: finishedAt.getTime() - startedAt.getTime(),
    repeats: options.repeats,
    snapshot: {
      model: options.draft.model,
      temperature: options.temperature,
      conditions: options.baseConditions,
      systemPrompt: options.draft.systemPrompt,
      systemPromptChars: options.draft.systemPrompt.length,
      promptHash: hashText(options.draft.systemPrompt),
      hasResponseSchema: Boolean(options.draft.responseSchema)
    },
    results,
    summary: summarize(results, judgeCost)
  }
}
